import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Crown, Target, DollarSign, Scale, Workflow, Users, Network, ShieldCheck, Bot,
  ChevronDown, TrendingUp, ArrowUpRight,
} from 'lucide-react';
import { AutonomyFeed } from '@/components/AutonomyFeed';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', saffron: '#FF9933' };
const ICONS = { Crown, Target, DollarSign, Scale, Workflow, Users, Network, ShieldCheck, Bot };
const SCALE_COLORS = { 0: '#C7CFD9', 1: '#E24B4A', 2: '#E8A020', 3: '#1A4C7C', 4: '#1D9E75' };

function pctColor(p) {
  if (p >= 80) return C.green;
  if (p >= 60) return C.blue;
  if (p >= 40) return C.amber;
  return C.red;
}

function fmtKpi(v, fmt) {
  if (fmt === 'currency') {
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${Number(v).toLocaleString()}`;
  }
  if (fmt === 'percent') return `${v}%`;
  if (fmt === 'score') return Number(v).toLocaleString();
  return Number(v).toLocaleString();
}

export default function CommandCenterPage() {
  const api = useApi();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(params.get('lens') || user?.lens || 'ceo');
  const [openCat, setOpenCat] = useState(null);

  useEffect(() => {
    api.get('/command/panels')
      .then(r => setData(r.data))
      .catch(err => console.error('Command error:', err?.message))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;
  if (!data) return <div className="text-sm" style={{ color: C.slate }}>Unable to load the Command Center.</div>;

  const panel = data.panels.find(p => p.id === selected) || data.panels[0];

  return (
    <div className="space-y-5" data-testid="command-center-page">
      {/* Lens switcher — grouped by workflow */}
      <div className="flex flex-wrap gap-1.5" data-testid="command-lens-switcher">
        {data.panels.map(p => {
          const Icon = ICONS[p.icon] || Crown;
          const active = p.id === selected;
          return (
            <button key={p.id} onClick={() => { setSelected(p.id); setOpenCat(null); }}
              data-testid={`command-lens-${p.id}`}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-all"
              style={active
                ? { background: C.navy, color: 'white', boxShadow: '0 2px 8px rgba(13,27,42,0.2)' }
                : { background: 'white', color: C.slate, border: '1px solid rgba(136,150,168,0.2)' }}>
              <Icon className="w-3.5 h-3.5" style={{ color: active ? C.saffron : C.slate }} />
              {p.abbr}
            </button>
          );
        })}
      </div>

      {/* Panel header */}
      <Card className="p-6 rounded-sm" style={{ background: '#0D1B2A', border: 'none' }} data-testid="command-panel-header">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[11px] font-bold tracking-wider" style={{ background: 'rgba(255,153,51,0.18)', color: C.saffron }}>{panel.abbr}</span>
              <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.55)' }}>{panel.group}</span>
            </div>
            <h2 className="text-2xl font-medium tracking-tight mt-2" style={{ color: 'white' }}>{panel.title}</h2>
            <p className="text-sm mt-1.5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.78)' }}>{panel.mandate}</p>
          </div>
          <div className="md:text-right shrink-0">
            <p className="text-[11px] uppercase tracking-[0.15em]" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Georgia, serif' }}>Owned FMI Score</p>
            <div className="flex items-end gap-2 md:justify-end mt-1">
              <span className="text-5xl font-light tracking-tight" style={{ color: 'white', fontVariantNumeric: 'tabular-nums' }} data-testid="command-owned-score">{panel.owned_points}</span>
              <span className="text-lg mb-1" style={{ color: 'rgba(255,255,255,0.45)' }}>/ {panel.owned_max}</span>
            </div>
            <p className="text-xs mt-1" style={{ color: C.teal }}>{panel.pct}% · target {panel.owned_target} · <span style={{ color: C.saffron }}>+{panel.gap} to go</span></p>
          </div>
        </div>
        <div className="mt-4 relative h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.12)' }}>
          <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${panel.pct}%`, background: 'linear-gradient(90deg, #00B4D8, #1D9E75)' }} />
          <div className="absolute top-[-3px] h-[14px] w-[2px]" style={{ left: `${(panel.owned_target / panel.owned_max) * 100}%`, background: C.saffron }} />
        </div>
      </Card>

      {/* Live KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="command-kpis">
        {panel.kpis.map((k, i) => (
          <Card key={i} className="p-4 rounded-sm micro-hover" style={{ border: '1px solid rgba(136,150,168,0.18)' }}>
            <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{k.label}</p>
            <p className="text-2xl font-light tracking-tight mt-1" style={{ color: C.navy, fontVariantNumeric: 'tabular-nums' }}>{fmtKpi(k.value, k.format)}</p>
          </Card>
        ))}
      </div>

      {/* Live autonomy feed — surfaced for the CEO (sees everything) and CAO (owns it) */}
      {(panel.id === 'ceo' || panel.id === 'cao') && <AutonomyFeed />}

      {/* Owned scorecard */}
      {panel.owned_categories.length > 0 && (
        <div data-testid="command-owned-scorecard">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate }}>Owned Scorecard ({panel.owned_categories.length} {panel.owned_categories.length === 1 ? 'category' : 'categories'})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {panel.owned_categories.map(cat => {
              const isOpen = openCat === cat.id;
              const targetPct = (cat.target / cat.weight) * 100;
              return (
                <Card key={cat.id} className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.14)' }}>
                  <button onClick={() => setOpenCat(isOpen ? null : cat.id)} className="w-full text-left p-4 hover:bg-[#FAFBFC] transition-colors">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold" style={{ color: C.navy }}>{cat.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono font-semibold" style={{ color: pctColor(cat.pct) }}>{cat.score}</span>
                        <span className="text-xs font-mono" style={{ color: C.slate }}>/ {cat.weight}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ color: C.slate }} />
                      </div>
                    </div>
                    <div className="mt-2.5 relative h-2 rounded-full overflow-hidden" style={{ background: '#EEF1F5' }}>
                      <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${cat.pct}%`, background: pctColor(cat.pct) }} />
                      <div className="absolute top-[-2px] h-[12px] w-[2px]" style={{ left: `${targetPct}%`, background: C.saffron }} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-3 pt-1 border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
                      {cat.criteria.map(cr => (
                        <div key={cr.id} className="flex items-center gap-2.5 py-1.5">
                          <span className="shrink-0 w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold font-mono" style={{ background: SCALE_COLORS[cr.score] + '20', color: SCALE_COLORS[cr.score] }}>{cr.score}</span>
                          <span className="text-xs flex-1" style={{ color: C.navy }}><span className="font-mono mr-1.5" style={{ color: C.slate }}>{cr.id}</span>{cr.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Cross-cutting (e.g. CISO) */}
      {panel.cross_cutting.length > 0 && (
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="command-crosscutting">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.slate }}>Cross-Cutting Custody ({panel.cross_cutting.length} criteria across the stack)</h3>
          <div className="space-y-1.5">
            {panel.cross_cutting.map(cr => (
              <div key={cr.id} className="flex items-start gap-2.5 py-1.5 border-b last:border-0" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                <span className="shrink-0 w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold font-mono mt-0.5" style={{ background: SCALE_COLORS[cr.score] + '20', color: SCALE_COLORS[cr.score] }}>{cr.score}</span>
                <div className="flex-1">
                  <span className="text-xs font-medium" style={{ color: C.navy }}><span className="font-mono mr-1.5" style={{ color: C.slate }}>{cr.id}</span>{cr.name}</span>
                  <span className="text-[11px] ml-2" style={{ color: C.slate }}>· {cr.category}</span>
                  <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{cr.note}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Top-leverage actions */}
      <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="command-actions">
        <div className="p-4 flex items-center gap-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
          <TrendingUp className="w-4 h-4" style={{ color: C.saffron }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>{panel.abbr} — Top-Leverage Actions</h3>
        </div>
        <div className="divide-y" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
          {panel.actions.map(a => (
            <div key={a.id} className="flex items-center gap-3 p-3.5">
              <span className="shrink-0 w-7 h-7 rounded flex items-center justify-center text-[11px] font-bold font-mono" style={{ background: SCALE_COLORS[a.score] + '20', color: SCALE_COLORS[a.score] }}>{a.score}</span>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium" style={{ color: C.navy }}><span className="font-mono mr-1.5 text-xs" style={{ color: C.slate }}>{a.id}</span>{a.name}</span>
                <p className="text-[11px]" style={{ color: C.slate }}>{a.category} · {a.note}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <ArrowUpRight className="w-3.5 h-3.5" style={{ color: C.red }} />
                <span className="text-xs font-mono font-semibold" style={{ color: C.red }}>+{a.weighted_gap}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
